import { useState, useEffect } from 'react';
import { geolocationService } from '../services/geolocation';
import { currencyService, CurrencyConversionResult } from '../services/currency';

interface CurrencyDisplayData {
  // Original price data
  originalAmount: number;
  originalCurrency: 'USD' | 'GEL';
  
  // Display data for user
  displayAmount: number;
  displayCurrency: 'USD' | 'GEL';
  
  // Conversion data (if Georgian user)
  isGeorgianUser: boolean;
  conversionData?: CurrencyConversionResult;
  
  // Payment options for Georgian users
  paymentOptions: Array<{
    currency: 'USD' | 'GEL';
    amount: number;
    label: string;
    isRecommended?: boolean;
  }>;
  
  // Formatting functions
  formatOriginal: () => string;
  formatDisplay: () => string;
  formatPaymentOption: (currency: 'USD' | 'GEL') => string;
}

export const useCurrency = (amount: number, currency: 'USD' | 'GEL' = 'USD') => {
  const [currencyData, setCurrencyData] = useState<CurrencyDisplayData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        setLoading(true);
        setError(null);

        // Detect if user is from Georgia
        const isFromGeorgia = await geolocationService.isFromGeorgia();
        
        let conversionData: CurrencyConversionResult | undefined;
        let paymentOptions: CurrencyDisplayData['paymentOptions'] = [];

        if (isFromGeorgia) {
          // For Georgian users, always show GEL option
          if (currency === 'USD') {
            // Convert USD to GEL for display
            conversionData = await currencyService.convertUSDToGEL(amount);
            
            // Provide both payment options
            paymentOptions = [
              {
                currency: 'GEL',
                amount: conversionData.convertedAmount,
                label: `Pay in Georgian Lari (${currencyService.formatCurrency(conversionData.convertedAmount, 'GEL')})`,
                isRecommended: true
              },
              {
                currency: 'USD',
                amount: amount,
                label: `Pay in US Dollars (${currencyService.formatCurrency(amount, 'USD')})`
              }
            ];
          } else {
            // Already in GEL, also show USD option
            conversionData = await currencyService.convertGELToUSD(amount);
            
            paymentOptions = [
              {
                currency: 'GEL',
                amount: amount,
                label: `Pay in Georgian Lari (${currencyService.formatCurrency(amount, 'GEL')})`,
                isRecommended: true
              },
              {
                currency: 'USD',
                amount: conversionData.convertedAmount,
                label: `Pay in US Dollars (${currencyService.formatCurrency(conversionData.convertedAmount, 'USD')})`
              }
            ];
          }
        } else {
          // Non-Georgian users - stick to original currency
          paymentOptions = [
            {
              currency: currency,
              amount: amount,
              label: `Pay in ${currency === 'USD' ? 'US Dollars' : 'Georgian Lari'} (${currencyService.formatCurrency(amount, currency)})`
            }
          ];
        }

        const data: CurrencyDisplayData = {
          originalAmount: amount,
          originalCurrency: currency,
          
          // For Georgians, display GEL; for others, display original
          displayAmount: isFromGeorgia && conversionData ? 
            (currency === 'USD' ? conversionData.convertedAmount : amount) : amount,
          displayCurrency: isFromGeorgia ? 'GEL' : currency,
          
          isGeorgianUser: isFromGeorgia,
          conversionData,
          paymentOptions,
          
          // Formatting functions
          formatOriginal: () => currencyService.formatCurrency(amount, currency),
          formatDisplay: () => {
            const displayAmt = isFromGeorgia && conversionData ? 
              (currency === 'USD' ? conversionData.convertedAmount : amount) : amount;
            const displayCur = isFromGeorgia ? 'GEL' : currency;
            return currencyService.formatCurrency(displayAmt, displayCur);
          },
          formatPaymentOption: (cur: 'USD' | 'GEL') => {
            const option = paymentOptions.find(opt => opt.currency === cur);
            return option ? currencyService.formatCurrency(option.amount, cur) : '';
          }
        };

        setCurrencyData(data);
        
        // Debug log
        console.log('💱 Currency data initialized:', {
          isGeorgian: isFromGeorgia,
          original: `${amount} ${currency}`,
          display: data.formatDisplay(),
          paymentOptions: paymentOptions.length
        });

      } catch (err) {
        console.error('Currency initialization error:', err);
        setError('Failed to load currency data');
        
        // Fallback data
        setCurrencyData({
          originalAmount: amount,
          originalCurrency: currency,
          displayAmount: amount,
          displayCurrency: currency,
          isGeorgianUser: false,
          paymentOptions: [{
            currency: currency,
            amount: amount,
            label: `Pay in ${currency === 'USD' ? 'US Dollars' : 'Georgian Lari'} (${currencyService.formatCurrency(amount, currency)})`
          }],
          formatOriginal: () => currencyService.formatCurrency(amount, currency),
          formatDisplay: () => currencyService.formatCurrency(amount, currency),
          formatPaymentOption: (cur) => currencyService.formatCurrency(amount, cur)
        });
      } finally {
        setLoading(false);
      }
    };

    initializeCurrency();
  }, [amount, currency]);

  return {
    currencyData,
    loading,
    error,
    
    // Convenience getters
    isGeorgianUser: currencyData?.isGeorgianUser ?? false,
    hasMultiplePaymentOptions: (currencyData?.paymentOptions?.length ?? 0) > 1,
    recommendedPaymentOption: currencyData?.paymentOptions?.find(opt => opt.isRecommended),
    
    // Quick formatters
    formatPrice: () => currencyData?.formatDisplay() ?? '',
    formatOriginalPrice: () => currencyData?.formatOriginal() ?? '',
    
    // Actions
    refreshRates: async () => {
      currencyService.clearCache();
      window.location.reload(); // Simple refresh for now
    }
  };
};
